<template>
    <div id="ticket-forwarding">
        <b-container class="mb-3" fluid>
            <a-card>
                <h5>{{ $t("ticket.ticketForwarding") }}</h5>
                <div class="d-flex w-100 align-items-end mb-2">
                    <div class="d-flex flex-column">
                        <div
                            v-tippy="{
                                followCursor: true,
                                placement: 'right',
                                theme: isDarkMode ? 'light-border' : 'google',
                            }"
                            :content="$t('ticket.ticketTagFilterDesc')"
                        >
                            <a-icon type="info-circle" />
                            <span>{{ $t("ticket.ticketTagFilter") }}</span>
                        </div>
                        <a-auto-complete
                            v-model="selectedTagFilter"
                            :data-source="tagFilters"
                            :placeholder="$t('ticket.ticketTagFilterPlaceHolder')"
                            @select="onSelect"
                            @search="onSearch"
                            @change="onChange"
                            style="width: 300px"
                        />
                    </div>

                    <div class="d-flex flex-column" style="flex: 1">
                        <div
                            v-tippy="{
                                followCursor: true,
                                placement: 'right',
                                theme: isDarkMode ? 'light-border' : 'google',
                            }"
                            :content="$t('ticket.ticketEmailsDesc')"
                        >
                            <a-icon type="info-circle" />
                            <span>{{ $t("ticket.ticketEmails") }}</span>
                        </div>
                        <a-select
                            mode="tags"
                            style="width: 100%"
                            :placeholder="$t('ticket.ticketEmailsPlaceHolder')"
                            @change="handleChange"
                            class="ml-2"
                            :value="selectedEmails"
                        >
                            <a-select-option v-for="user in users" :key="user.email">
                                {{
                                    $t("ticket.forwardingUserOption", {
                                        name: user.name.toUpperCase(),
                                        email: user.email,
                                    })
                                }}
                            </a-select-option>
                        </a-select>
                    </div>

                    <a-button :disabled="loading" @click="handleSave" type="primary" class="ml-2 saveBtn">
                        {{ $t("ticket.save") }}
                    </a-button>
                </div>
            </a-card>

            <a-divider></a-divider>
            <a-card>
                <h5>{{ $t("ticket.ticketList") }}</h5>
                <a-table
                    :columns="columns"
                    :dataSource="items"
                    :locale="{
                        emptyText: $t('ticket.noEmailTicketNotifications'),
                    }"
                    :pagination="false"
                    class="custom-table2"
                    rowKey="_id"
                    size="small"
                >
                    <div slot="_id" slot-scope="_id, item">
                        <div class="d-flex w-100 justify-content-start">
                            <a-button
                                class="actionButton d-flex align-items-center text-danger"
                                size="small"
                                @click="deleteItem(item)"
                            >
                                <span style="margin-right: 3px; display: block; font-size: 12px">{{
                                    $t("ticket.delete")
                                }}</span>
                                <a-icon type="delete" />
                            </a-button>
                        </div>
                    </div>
                </a-table>
            </a-card>
        </b-container>
    </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";
import { log2 } from "../../../../utils/log2";
import SearchIcon from "../../../../components/svg/SearchIcon.vue";
import Pagination from "../../../../components/Pagination.vue";
import {
    addEmailTicketNotification,
    getTicketNotifications,
    removeEmailTicketNotification,
} from "../../../../api/ticket";
import { tableSorter } from "../../../../utils/utils";
import * as _ from "lodash";
import { CHATBOT_API } from "../../../../api/chatbotv2";

const log = log2("TicketForwarding");
export default {
    name: "TicketForwarding",
    components: {
        Pagination,
        SearchIcon,
    },
    props: {},
    data() {
        return {
            items: [],
            preDefinedTagFilters: ["*", "offline_message"],
            tagFilters: ["*"],
            selectedTagFilter: undefined,
            selectedEmails: undefined,
            loading: true,
            newTicket: {
                email: undefined,
                tagFilter: "*",
            },
            columns: [
                {
                    title: this.$t("message.email"),
                    dataIndex: "email",
                    key: "email",
                    ...tableSorter("email", "string", this.currentSort),
                },
                {
                    dataIndex: "tagFilter",
                    title: this.$t("ticket.ticketTagFilter"),
                    key: "tagFilter",
                    ...tableSorter("tagFilter", "string", this.currentSort),
                },
                {
                    dataIndex: "_id",
                    title: this.$t("ticket.transactions"),
                    scopedSlots: { customRender: "_id" },
                    slot: "_id",
                    width: "15%",
                    ...tableSorter("_id", "string", this.currentSort),
                },
            ],
            users: [],
        };
    },
    computed: {
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
        ...mapState({}),
    },
    methods: {
        ...mapActions({
            getUsers: "user/getUsers",
        }),
        validateEmail(email) {
            const re =
                /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return re.test(email);
        },
        handleChange(value) {
            log(`selected ${value}`, value, typeof value, value.split);
            const emails = value.map((x) => ({
                email: x,
                valid: this.validateEmail(x),
            }));
            if (emails.find((x) => !x.valid)) {
                this.$toasted.error(this.$t("ticket.emailIsNotValid"), {
                    duration: 1000,
                    position: "top-right",
                });
            } else {
                this.selectedEmails = emails.filter((x) => x.valid).map((x) => x.email);
            }
        },
        onSearch(searchText) {
            log("onSearch", searchText);
            this.tagFilters = !searchText ? this.preDefinedTagFilters : [searchText, ...this.preDefinedTagFilters];
        },
        onSelect(value) {
            log("onSelect", value);
        },
        onChange(value) {
            log("onChange", value);
        },
        deleteItem(item) {
            log("delete", item);
            removeEmailTicketNotification(item._id)
                .then((res) => {
                    if (res.data) {
                        this.$toasted.success(this.$t("ticket.emailNotificationDeleted"), {
                            duration: 2000,
                            position: "top-right",
                        });
                        this.items = this.items.filter((x) => x._id !== item._id);
                    } else {
                        this.$toasted.error(this.$t("ticket.emailNotificationDeleteError"), {
                            duration: 1000,
                            position: "top-right",
                        });
                    }
                })
                .catch((err) => {
                    this.$toasted.error(this.$t("ticket.emailNotificationDeleteError"), {
                        duration: 1000,
                        position: "top-right",
                    });
                });
        },
        openCreateFormModal() {
            this.$bvModal.show("create-form");
        },
        reset() {
            this.newTicket = {
                email: undefined,
                tagFilter: "*",
            };
        },
        handleSave() {
            if (!this.selectedTagFilter) {
                this.$toasted.error(this.$t("ticket.tagFilterRequired"), {
                    duration: 2000,
                    position: "top-right",
                });
                return;
            }

            if (!this.selectedEmails.length) {
                this.$toasted.error(this.$t("ticket.emailsRequired"), {
                    duration: 2000,
                    position: "top-right",
                });
                return;
            }
            this.loading = true;
            addEmailTicketNotification(this.selectedEmails, this.selectedTagFilter)
                .then((response) => {
                    this.$toasted.success(this.$t("ticket.emailNotificationCreated"), {
                        duration: 2000,
                        position: "top-right",
                    });
                    this.items = response.data;
                })
                .catch((err) => {
                    console.error(err);
                    this.$toasted.error(this.$t("ticket.emailNotificationCreateError"), {
                        duration: 2000,
                        position: "top-right",
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        get() {
            this.loading = true;
            getTicketNotifications()
                .then((response) => {
                    this.items = response.data;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        fetchUsers() {
            this.getUsers({
                perPage: 200,
                page: 1,
                query: undefined,
                updateStore: false,
            }).then((res) => {
                log("users fetched", res.data);
                this.users = res.data.data;
            });
        },
        fetchTicketTags() {
            CHATBOT_API.getAllChatBotTicketTags().then((res) => {
                this.preDefinedTagFilters = _.uniq(["*", "offline_message", ...res.data]);
                this.tagFilters = this.preDefinedTagFilters;
            });
        },
    },
    mounted() {
        moment.locale(this.$i18n.locale);
        this.get();
        this.fetchUsers();
        this.fetchTicketTags();
    },
    beforeDestroy() {},
    watch: {},
};
</script>

<style lang="scss">
#create-form___BV_modal_body_ {
    overflow-y: none;
}

#ticket-forwarding {
    .saveBtn {
        align-self: flex-end;
        margin-left: 13px !important;
    }
}
</style>
